import React, { createContext, useContext, useState, useCallback } from "react";
import PropTypes from "prop-types";

export const ProyeccionContext = createContext(null);

const ProyeccionProvider = ({ children }) => {
  // Estado existente
  const [proyeccionState, setProyeccionState] = useState({
    ultimaCuota: 0,
    ultimaFecha: null,
    cuotasPorClase: {},
    fechasPorClase: {},
    configuraciones: {},
    clasesModificadas: new Set(),
    proyeccionActual: null,
  });

  // Nuevos estados para vinculación
  const [clasesVinculadas, setClasesVinculadas] = useState({});
  const [disponibleRestante, setDisponibleRestante] = useState(null);
  const [vinculacionesEnProceso, setVinculacionesEnProceso] = useState(new Set());

  // Funciones existentes
  const actualizarEstadoClase = useCallback((clase, ultimaCuota, ultimaFecha) => {
    setProyeccionState((prev) => ({
      ...prev,
      ultimaCuota,
      ultimaFecha,
      cuotasPorClase: {
        ...prev.cuotasPorClase,
        [clase]: ultimaCuota,
      },
      fechasPorClase: {
        ...prev.fechasPorClase,
        [clase]: ultimaFecha,
      },
    }));
  }, []);

  const actualizarProyeccion = useCallback((nuevaProyeccion) => {
    setProyeccionState((prev) => ({
      ...prev,
      proyeccionActual: nuevaProyeccion,
    }));
  }, []);

  const actualizarConfiguracion = useCallback(
    (clase, nuevaConfiguracion, clases) => {
      setProyeccionState((prev) => {
        const claseIndex = clases.indexOf(clase);
        const clasesAfectadas = clases.slice(claseIndex + 1);
        const nuevasClasesModificadas = new Set([...clasesAfectadas]);

        // Agregar clases vinculadas a la lista de modificadas
        if (clasesVinculadas[clase]) {
          clasesVinculadas[clase].forEach((claseVinculada) => {
            nuevasClasesModificadas.add(claseVinculada);
          });
        }

        return {
          ...prev,
          configuraciones: {
            ...prev.configuraciones,
            [clase]: nuevaConfiguracion,
          },
          clasesModificadas: nuevasClasesModificadas,
        };
      });
    },
    [clasesVinculadas]
  );

  const necesitaActualizacion = useCallback(
    (clase) => {
      return proyeccionState.clasesModificadas.has(clase);
    },
    [proyeccionState.clasesModificadas]
  );

  const marcarActualizada = useCallback((clase) => {
    setProyeccionState((prev) => {
      const nuevasClasesModificadas = new Set(prev.clasesModificadas);
      nuevasClasesModificadas.delete(clase);
      return {
        ...prev,
        clasesModificadas: nuevasClasesModificadas,
      };
    });
  }, []);

  const resetProyeccion = useCallback(() => {
    setProyeccionState({
      ultimaCuota: 0,
      ultimaFecha: null,
      cuotasPorClase: {},
      fechasPorClase: {},
      configuraciones: {},
      clasesModificadas: new Set(),
    });
    setClasesVinculadas({});
    setDisponibleRestante(null);
    setVinculacionesEnProceso(new Set());
  }, []);

  // Nuevas funciones para vinculación
  const puedeVincularClases = useCallback(
    (clase1, clase2, configClases, disponible) => {
      // Evitar vinculación si alguna clase ya está en proceso
      if (vinculacionesEnProceso.has(clase1) || vinculacionesEnProceso.has(clase2)) {
        return {
          puede: false,
          mensaje: "Una de las clases ya está en proceso de vinculación",
        };
      }

      if (!configClases[clase1] || !configClases[clase2]) {
        return {
          puede: false,
          mensaje: "Configuración no disponible para alguna de las clases",
        };
      }

      // Verificar modo de cálculo
      if (configClases[clase1].modo_calculo !== configClases[clase2].modo_calculo) {
        return {
          puede: false,
          mensaje: "Las clases tienen diferentes modos de cálculo",
        };
      }

      // Verificar número de cuotas si es POR_CUOTAS
      if (configClases[clase1].modo_calculo === "POR_CUOTAS") {
        if (configClases[clase1].numero_cuotas !== configClases[clase2].numero_cuotas) {
          return {
            puede: false,
            mensaje: "Las clases tienen diferente número de cuotas",
          };
        }
      }

      // Verificar disponible
      const sumaCuotas =
        (configClases[clase1].cuotaBase || 0) + (configClases[clase2].cuotaBase || 0);
      if (sumaCuotas > disponible) {
        return {
          puede: false,
          mensaje: "La suma de cuotas excede el monto disponible",
        };
      }

      return { puede: true };
    },
    [vinculacionesEnProceso]
  );

  const vincularClases = useCallback(async (clase1, clase2, isInit = false) => {
    try {
      if (!isInit) {
        // Solo establecer vinculaciones en proceso para operaciones normales
        setVinculacionesEnProceso((prev) => new Set([...prev, clase1, clase2]));
      }

      setClasesVinculadas((prev) => {
        const nuevasVinculaciones = { ...prev };

        // Para inicialización, solo establecer la vinculación en una dirección
        if (isInit) {
          if (!nuevasVinculaciones[clase1]) {
            nuevasVinculaciones[clase1] = [];
          }
          if (!nuevasVinculaciones[clase1].includes(clase2)) {
            nuevasVinculaciones[clase1].push(clase2);
          }
          return nuevasVinculaciones;
        }

        // Para vinculaciones normales, establecer en ambas direcciones
        if (!nuevasVinculaciones[clase1]) {
          nuevasVinculaciones[clase1] = [];
        }
        if (!nuevasVinculaciones[clase1].includes(clase2)) {
          nuevasVinculaciones[clase1].push(clase2);
        }

        if (!nuevasVinculaciones[clase2]) {
          nuevasVinculaciones[clase2] = [];
        }
        if (!nuevasVinculaciones[clase2].includes(clase1)) {
          nuevasVinculaciones[clase2].push(clase1);
        }

        return nuevasVinculaciones;
      });

      if (!isInit) {
        // Solo marcar clases para actualización en operaciones normales
        setProyeccionState((prev) => ({
          ...prev,
          clasesModificadas: new Set([...prev.clasesModificadas, clase1, clase2]),
        }));
      }

      return { success: true };
    } catch (error) {
      console.error("Error al vincular clases:", error);
      return { error: "Error al vincular las clases" };
    } finally {
      if (!isInit) {
        // Solo limpiar vinculaciones en proceso para operaciones normales
        setVinculacionesEnProceso((prev) => {
          const nuevo = new Set(prev);
          nuevo.delete(clase1);
          nuevo.delete(clase2);
          return nuevo;
        });
      }
    }
  }, []);

  const desvincularClases = useCallback((clase1, clase2) => {
    setClasesVinculadas((prev) => {
      const nuevasVinculaciones = { ...prev };

      if (nuevasVinculaciones[clase1]) {
        nuevasVinculaciones[clase1] = nuevasVinculaciones[clase1].filter((c) => c !== clase2);
        if (nuevasVinculaciones[clase1].length === 0) {
          delete nuevasVinculaciones[clase1];
        }
      }

      if (nuevasVinculaciones[clase2]) {
        nuevasVinculaciones[clase2] = nuevasVinculaciones[clase2].filter((c) => c !== clase1);
        if (nuevasVinculaciones[clase2].length === 0) {
          delete nuevasVinculaciones[clase2];
        }
      }

      return nuevasVinculaciones;
    });

    // Marcar clases para actualización
    setProyeccionState((prev) => ({
      ...prev,
      clasesModificadas: new Set([...prev.clasesModificadas, clase1, clase2]),
    }));
  }, []);

  const actualizarDisponibleRestante = useCallback((disponible) => {
    if (setDisponibleRestante) {
      setDisponibleRestante(disponible);
    }
  }, []);

  return (
    <ProyeccionContext.Provider
      value={{
        // Estados y funciones existentes
        proyeccionState,
        actualizarEstadoClase,
        actualizarConfiguracion,
        necesitaActualizacion,
        marcarActualizada,
        resetProyeccion,
        actualizarProyeccion,

        // Nuevos estados y funciones para vinculación
        clasesVinculadas: clasesVinculadas || {},
        disponibleRestante: disponibleRestante || null,
        puedeVincularClases,
        vincularClases,
        desvincularClases,
        actualizarDisponibleRestante,
        vinculacionesEnProceso: Array.from(vinculacionesEnProceso || []),
      }}
    >
      {children}
    </ProyeccionContext.Provider>
  );
};

ProyeccionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useProyeccion = () => {
  const context = useContext(ProyeccionContext);
  if (!context) {
    throw new Error("useProyeccion debe usarse dentro de un ProyeccionProvider");
  }
  return context;
};

export { ProyeccionProvider, useProyeccion };
